<template>
  <AccessDenied
    v-if="!permissaoTela.visualizar"
    msg="Você não tem permissão para visualizar esta tela. Caso precise de acesso, procure o suporte."
  />

  <div v-else class="sala-ambiente">
    <!--
      O formulário existe por causa do Salvar: o botão do `BarraBotoes` é `type="submit"`.
      `@submit.prevent` porque quem grava é o método, não o navegador.
    -->
    <form novalidate @submit.prevent="salvar">
      <!-- Região viva permanente (WCAG 4.1.3): criada junto com a mensagem, não seria anunciada. -->
      <div class="visually-hidden" role="status" aria-live="polite">{{ anuncio }}</div>

      <BarraBotoes
        :key="chaveBarra"
        :loading-save="salvando"
        :id-registro-carregado="idCarregado"
        :grid-view-link="'/gestao/sala'"
        :show-imprimir="false"
        :bloquear-botao-salvar="somenteLeitura"
        @limpar="novoAmbiente"
        @excluir="excluir"
      />

      <p v-if="carregando" class="text-muted">Carregando o ambiente...</p>

      <!-- ======================================================== identificação -->
      <section class="card" aria-labelledby="sala-identificacao-titulo">
        <div class="card-body">
          <h2 id="sala-identificacao-titulo" class="h5">Identificação e localização</h2>

          <div class="row g-3">
            <div class="col-12 col-sm-4 col-lg-2">
              <label for="sala-numero" class="form-label">
                Identificação <span aria-hidden="true">*</span>
                <span class="visually-hidden">(obrigatório)</span>
              </label>
              <input
                id="sala-numero"
                v-model="model.numero"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': erros.numero }"
                maxlength="5"
                required
                autocomplete="off"
                :readonly="somenteLeitura"
                :aria-invalid="erros.numero ? 'true' : undefined"
                :aria-describedby="erros.numero ? 'sala-numero-erro' : 'sala-numero-ajuda'"
              />
              <div v-if="erros.numero" id="sala-numero-erro" class="invalid-feedback d-block">
                {{ erros.numero }}
              </div>
              <div v-else id="sala-numero-ajuda" class="form-text">Até 5 caracteres.</div>
            </div>

            <div class="col-12 col-sm-8 col-lg-4">
              <label for="sala-bloco" class="form-label">
                Localização <span aria-hidden="true">*</span>
                <span class="visually-hidden">(obrigatório)</span>
              </label>
              <input
                id="sala-bloco"
                v-model="model.bloco"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': erros.bloco }"
                maxlength="50"
                required
                autocomplete="off"
                :readonly="somenteLeitura"
                :aria-invalid="erros.bloco ? 'true' : undefined"
                :aria-describedby="erros.bloco ? 'sala-bloco-erro' : 'sala-bloco-ajuda'"
              />
              <div v-if="erros.bloco" id="sala-bloco-erro" class="invalid-feedback d-block">
                {{ erros.bloco }}
              </div>
              <div v-else id="sala-bloco-ajuda" class="form-text">
                Bloco, andar, ala ou outra referência (até 50 caracteres).
              </div>
            </div>

            <div class="col-12 col-md-6 col-lg-3">
              <DropDownConsulte
                id="sala-tipo"
                v-model.number="model.gesFuncaoOriginalId"
                :array="tiposAmbiente"
                row-label="texto"
                row-value="valor"
                label="Tipo de ambiente"
                placeholder="Selecione o tipo"
                :disabled="somenteLeitura"
              />
            </div>

            <div class="col-12 col-md-6 col-lg-3">
              <DropDownConsulte
                id="sala-utilizacao"
                v-model.number="model.gesUtilizacaoId"
                :array="utilizacoes"
                row-label="texto"
                row-value="valor"
                label="Utilização atual"
                placeholder="Selecione a utilização"
                :disabled="somenteLeitura"
              />
            </div>

            <div class="col-12 col-md-6 col-lg-3">
              <DropDownConsulte
                id="sala-forma-ocupacao"
                v-model.number="model.formaOcupacao"
                :array="formasOcupacao"
                row-label="texto"
                row-value="valor"
                label="Forma de ocupação"
                placeholder="Selecione a forma"
                :disabled="somenteLeitura"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- ================================================= dimensões e capacidade -->
      <section class="card" aria-labelledby="sala-capacidade-titulo">
        <div class="card-body">
          <h2 id="sala-capacidade-titulo" class="h5">Dimensões e capacidade</h2>

          <div class="row g-3">
            <div class="col-12 col-sm-6 col-lg-3">
              <label for="sala-area" class="form-label">Área (m²)</label>
              <input
                id="sala-area"
                v-model="textoArea"
                type="number"
                inputmode="decimal"
                class="form-control"
                :class="{ 'is-invalid': erros.area }"
                min="0.01"
                :max="AREA_MAXIMA"
                step="0.01"
                :readonly="somenteLeitura"
                :aria-invalid="erros.area ? 'true' : undefined"
                :aria-describedby="erros.area ? 'sala-area-erro' : 'sala-area-ajuda'"
                @input="aoMudarDimensoes"
              />
              <div v-if="erros.area" id="sala-area-erro" class="invalid-feedback d-block">
                {{ erros.area }}
              </div>
              <div v-else id="sala-area-ajuda" class="form-text">Até duas casas decimais.</div>
            </div>

            <div class="col-12 col-sm-6 col-lg-3">
              <label for="sala-coeficiente" class="form-label">Coeficiente (alunos por m²)</label>
              <input
                id="sala-coeficiente"
                v-model="textoCoeficiente"
                type="number"
                inputmode="decimal"
                class="form-control"
                :class="{ 'is-invalid': erros.coeficienteAlunoM2 }"
                min="0.0001"
                :max="COEFICIENTE_MAXIMO"
                step="0.0001"
                :readonly="somenteLeitura"
                :aria-invalid="erros.coeficienteAlunoM2 ? 'true' : undefined"
                :aria-describedby="
                  erros.coeficienteAlunoM2 ? 'sala-coeficiente-erro' : 'sala-coeficiente-ajuda'
                "
                @input="aoMudarDimensoes"
              />
              <div
                v-if="erros.coeficienteAlunoM2"
                id="sala-coeficiente-erro"
                class="invalid-feedback d-block"
              >
                {{ erros.coeficienteAlunoM2 }}
              </div>
              <div v-else id="sala-coeficiente-ajuda" class="form-text">
                Quantos alunos cabem em cada m² (ex.: 0,8). Até quatro casas decimais.
              </div>
            </div>

            <div class="col-12 col-sm-6 col-lg-3">
              <label for="sala-capacidade" class="form-label">Capacidade (alunos)</label>
              <input
                id="sala-capacidade"
                v-model="textoCapacidade"
                type="number"
                inputmode="numeric"
                class="form-control"
                :class="{ 'is-invalid': erros.capacidade }"
                min="0"
                :max="CAPACIDADE_MAXIMA"
                step="1"
                :readonly="somenteLeitura"
                :aria-invalid="erros.capacidade ? 'true' : undefined"
                :aria-describedby="
                  erros.capacidade ? 'sala-capacidade-erro sala-capacidade-situacao' : 'sala-capacidade-situacao'
                "
                @input="aoDigitarCapacidade"
              />
              <div v-if="erros.capacidade" id="sala-capacidade-erro" class="invalid-feedback d-block">
                {{ erros.capacidade }}
              </div>
            </div>

            <div class="col-12 col-sm-6 col-lg-3">
              <label for="sala-capacidade-real" class="form-label">Capacidade real (alunos)</label>
              <input
                id="sala-capacidade-real"
                v-model="textoCapacidadeReal"
                type="number"
                inputmode="numeric"
                class="form-control"
                :class="{ 'is-invalid': erros.capacidadeReal }"
                min="0"
                :max="CAPACIDADE_MAXIMA"
                step="1"
                :readonly="somenteLeitura"
                :aria-invalid="erros.capacidadeReal ? 'true' : undefined"
                :aria-describedby="erros.capacidadeReal ? 'sala-capacidade-real-erro' : undefined"
              />
              <div
                v-if="erros.capacidadeReal"
                id="sala-capacidade-real-erro"
                class="invalid-feedback d-block"
              >
                {{ erros.capacidadeReal }}
              </div>
            </div>

            <!--
              Situação da capacidade: SEMPRE no DOM (o texto muda), para que o leitor de tela
              anuncie a mudança quando a área ou o coeficiente forem alterados.
            -->
            <div class="col-12">
              <div
                id="sala-capacidade-situacao"
                class="sala-capacidade-situacao"
                :class="capacidadeDiverge ? 'sala-capacidade-situacao--aviso' : ''"
                aria-live="polite"
              >
                <i
                  class="mdi me-1"
                  :class="capacidadeDiverge ? 'mdi-alert-outline' : 'mdi-calculator-variant-outline'"
                  aria-hidden="true"
                ></i>
                <strong v-if="capacidadeDiverge">Capacidade ajustada manualmente.</strong>
                {{ textoSituacaoCapacidade }}
              </div>
              <button
                v-if="capacidadeDiverge && !somenteLeitura"
                type="button"
                class="btn btn-sm btn-outline-primary mt-2"
                @click="usarCapacidadeCalculada"
              >
                <i class="mdi mdi-calculator-variant-outline me-1" aria-hidden="true"></i>
                Usar a capacidade calculada ({{ textoAlunos(capacidadeCalculada ?? 0) }})
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- ============================================================== situação -->
      <section class="card" aria-labelledby="sala-situacao-titulo">
        <div class="card-body">
          <h2 id="sala-situacao-titulo" class="h5">Situação</h2>
          <div class="d-flex flex-wrap gap-4">
            <div class="form-check">
              <input
                id="sala-recebe-turma"
                v-model="recebeTurma"
                class="form-check-input"
                type="checkbox"
                :disabled="somenteLeitura"
              />
              <label for="sala-recebe-turma" class="form-check-label">Recebe turma</label>
            </div>
            <div class="form-check">
              <input
                id="sala-requer-adequacao"
                v-model="requerAdequacao"
                class="form-check-input"
                type="checkbox"
                :disabled="somenteLeitura"
              />
              <label for="sala-requer-adequacao" class="form-check-label">Requer adequação</label>
            </div>
            <div class="form-check">
              <input
                id="sala-ativo"
                v-model="ativo"
                class="form-check-input"
                type="checkbox"
                :disabled="somenteLeitura"
              />
              <label for="sala-ativo" class="form-check-label">Ativo</label>
            </div>
          </div>
        </div>
      </section>
    </form>

    <!-- ======================================== documentos (fora do <form> do ambiente) -->
    <AnexosAmbiente v-if="idNumerico" :sala-id="idNumerico" :tipos-documento="tiposDocumento" />
    <section v-else class="card" aria-labelledby="sala-documentos-titulo">
      <div class="card-body">
        <h2 id="sala-documentos-titulo" class="h5">Documentos do ambiente</h2>
        <p class="mb-0">Salve o ambiente para anexar documentos a ele.</p>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import AccessDenied from '@/views/auth/AccessDenied.vue';
  import BarraBotoes from '@/components/formulario/BarraBotoes.vue';
  import DropDownConsulte from '@/components/dropdownconsulte/DropDownConsulte.vue';
  import AnexosAmbiente from './components/AnexosAmbiente.vue';
  import { SalaModel } from '@/models/gestao/sala/sala.model';
  import TextoValor from '@/models/texto-valor';
  import salaService from '@/services/gestao/sala/sala.service';
  import funcaoOriginalSalaService from '@/services/gestao/funcaooriginalsala/funcaooriginalsala.service';
  import utilizacaoSalaService from '@/services/gestao/utilizacaosala/utilizacaosala.service';
  import toastService from '@/services/globals/toast.service';
  import { eTipoPermissao } from '@/enums/ePermissoes';
  import {
    PERMISSAO_TELA_INJECT_KEY,
    PermissaoTela,
    resolverPermissaoTela,
  } from '@/composables/usePermissaoTela/usePermissaoTela';
  import { pluralizar } from '@/uteis/texto';
  import { useStore } from '@/store';
  import {
    AREA_MAXIMA,
    CAPACIDADE_MAXIMA,
    CASAS_AREA,
    CASAS_COEFICIENTE,
    COEFICIENTE_MAXIMO,
    calcularCapacidade,
    casasDecimais,
  } from './capacidade';

  type CampoSala = 'numero' | 'bloco' | 'area' | 'coeficienteAlunoM2' | 'capacidade' | 'capacidadeReal';

  /** Ordem dos campos na tela — o foco vai para o primeiro com erro. */
  const ORDEM_CAMPOS: [CampoSala, string][] = [
    ['numero', 'sala-numero'],
    ['bloco', 'sala-bloco'],
    ['area', 'sala-area'],
    ['coeficienteAlunoM2', 'sala-coeficiente'],
    ['capacidade', 'sala-capacidade'],
    ['capacidadeReal', 'sala-capacidade-real'],
  ];

  /** "" e qualquer coisa que não seja número viram `null`. */
  function paraNumero(valor: unknown): number | null {
    if (valor === null || valor === undefined || valor === '') return null;
    const numero = typeof valor === 'number' ? valor : Number(String(valor).replace(',', '.'));
    return Number.isFinite(numero) ? numero : null;
  }

  function paraTexto(valor: number | null | undefined): string {
    return valor == null ? '' : String(valor);
  }

  function formatarNumero(valor: number, casas: number): string {
    return valor.toLocaleString('pt-BR', { maximumFractionDigits: casas });
  }

  /**
   * Cadastro de Salas e Ambientes — o espaço físico da escola: localização, tipo, forma de
   * ocupação, área, coeficiente de alunos por m², capacidade (calculada e ajustável) e os
   * documentos do ambiente. Ver docs/salas-ambientes/README.md.
   *
   * Permissão: a da rota (`SALASEAMBIENTESNOVO_MENU_300900101`), resolvida aqui e provida aos
   * filhos. Salvar é `incluir` (novo) ou `alterar` (existente) — o `BarraBotoes` já decide;
   * excluir é `excluir`; os documentos seguem a mesma permissão pelo inject.
   */
  export default defineComponent({
    name: 'SalaInclusaoEdicao',
    components: { AccessDenied, BarraBotoes, DropDownConsulte, AnexosAmbiente },
    provide() {
      return {
        [PERMISSAO_TELA_INJECT_KEY]: this.permissaoTela,
      };
    },
    data() {
      return {
        AREA_MAXIMA,
        COEFICIENTE_MAXIMO,
        CAPACIDADE_MAXIMA,
        model: new SalaModel() as SalaModel,
        // Os campos numéricos são editados como TEXTO: `v-model.number` num campo vazio devolve
        // "" e não `null`, e a API recusa "" num decimal.
        textoArea: '',
        textoCoeficiente: '',
        textoCapacidade: '',
        textoCapacidadeReal: '',
        erros: {} as Partial<Record<CampoSala, string>>,
        tiposAmbiente: [] as TextoValor[],
        utilizacoes: [] as TextoValor[],
        formasOcupacao: [] as { valor: number; texto: string }[],
        tiposDocumento: [] as { valor: number; texto: string }[],
        salvando: false,
        carregando: false,
        anuncio: '',
        chaveBarra: 0,
      };
    },
    computed: {
      permissaoTela(): PermissaoTela {
        return resolverPermissaoTela(this.$route.meta?.permissions as eTipoPermissao[] | undefined);
      },
      idCarregado(): string {
        const id = this.$route.params.id;
        return id ? String(id) : '';
      },
      idNumerico(): number {
        return Number(this.idCarregado) || 0;
      },
      /** Novo registro sem `incluir`, ou registro existente sem `alterar`: só consulta. */
      somenteLeitura(): boolean {
        return this.idNumerico ? !this.permissaoTela.alterar : !this.permissaoTela.incluir;
      },
      capacidadeCalculada(): number | null {
        return calcularCapacidade(paraNumero(this.textoArea), paraNumero(this.textoCoeficiente));
      },
      /** Há cálculo e a capacidade gravada/digitada é outra. */
      capacidadeDiverge(): boolean {
        return (
          this.capacidadeCalculada !== null &&
          !!this.model.capacidadeAjusteManual &&
          paraNumero(this.textoCapacidade) !== this.capacidadeCalculada
        );
      },
      textoSituacaoCapacidade(): string {
        const calculada = this.capacidadeCalculada;
        if (calculada === null) {
          return 'Informe a área e o coeficiente para a capacidade ser calculada automaticamente, ou digite a capacidade.';
        }
        const area = formatarNumero(paraNumero(this.textoArea) ?? 0, CASAS_AREA);
        const coeficiente = formatarNumero(paraNumero(this.textoCoeficiente) ?? 0, CASAS_COEFICIENTE);
        const conta = `${area} m² × ${coeficiente} aluno por m² = ${this.textoAlunos(calculada)}`;
        if (this.capacidadeDiverge) {
          return `O cálculo pela área e pelo coeficiente (${conta}) não é usado enquanto o ajuste valer.`;
        }
        return `Calculada automaticamente: ${conta} (arredondado para baixo). Você pode alterá-la.`;
      },
      recebeTurma: {
        get(): boolean {
          return this.model.recebeTurma === 1;
        },
        set(valor: boolean) {
          this.model.recebeTurma = valor ? 1 : 0;
        },
      },
      requerAdequacao: {
        get(): boolean {
          return this.model.requerAdequacao === 1;
        },
        set(valor: boolean) {
          this.model.requerAdequacao = valor ? 1 : 0;
        },
      },
      ativo: {
        get(): boolean {
          return this.model.ativo !== 0;
        },
        set(valor: boolean) {
          this.model.ativo = valor ? 1 : 0;
        },
      },
    },
    watch: {
      '$route.params.id': {
        handler() {
          this.carregar();
        },
      },
    },
    mounted() {
      this.carregarListas();
      this.carregar();
    },
    methods: {
      textoAlunos(quantidade: number): string {
        return pluralizar(quantidade, 'aluno', 'alunos');
      },

      async carregarListas() {
        const [tipos, utilizacoes, listas] = await Promise.allSettled([
          funcaoOriginalSalaService.DropDownList(),
          utilizacaoSalaService.DropDownList(),
          salaService.listas(),
        ]);
        if (tipos.status === 'fulfilled') this.tiposAmbiente = tipos.value;
        if (utilizacoes.status === 'fulfilled') this.utilizacoes = utilizacoes.value;
        if (listas.status === 'fulfilled') {
          this.formasOcupacao = listas.value.formasOcupacao;
          this.tiposDocumento = listas.value.tiposDocumento;
        }
      },

      aplicarModelo(sala: SalaModel) {
        this.model = Object.assign(new SalaModel(), sala, {
          gesFuncaoOriginalId: sala.gesFuncaoOriginalId || null,
          gesUtilizacaoId: sala.gesUtilizacaoId || null,
          formaOcupacao: sala.formaOcupacao || null,
          capacidadeAjusteManual: !!sala.capacidadeAjusteManual,
          recebeTurma: sala.recebeTurma ?? 0,
          requerAdequacao: sala.requerAdequacao ?? 0,
          ativo: sala.ativo ?? 1,
        });
        this.textoArea = paraTexto(sala.area);
        this.textoCoeficiente = paraTexto(sala.coeficienteAlunoM2);
        this.textoCapacidade = paraTexto(sala.capacidade);
        this.textoCapacidadeReal = paraTexto(sala.capacidadeReal);
        this.erros = {};
        this.chaveBarra += 1;
      },

      async carregar() {
        if (!this.idNumerico) {
          this.aplicarModelo(new SalaModel());
          return;
        }
        this.carregando = true;
        try {
          const sala = await salaService.get(this.idNumerico);
          if (!sala) {
            toastService.alertaErro('O ambiente não foi encontrado. Ele pode ter sido excluído.');
            this.$router.replace({ name: 'sala_create' });
            return;
          }
          this.aplicarModelo(sala);
        } finally {
          this.carregando = false;
        }
      },

      /**
       * Área ou coeficiente mudou. Sem ajuste manual, a capacidade acompanha o cálculo (e fica
       * vazia quando não há o que calcular); com ajuste, o número digitado é preservado e o
       * aviso mostra quanto daria o cálculo.
       */
      aoMudarDimensoes() {
        if (this.model.capacidadeAjusteManual) return;
        const calculada = this.capacidadeCalculada;
        this.textoCapacidade = calculada === null ? '' : String(calculada);
      },

      /** O usuário digitou a capacidade: vale como ajuste se for diferente do cálculo. */
      aoDigitarCapacidade() {
        const digitada = paraNumero(this.textoCapacidade);
        const calculada = this.capacidadeCalculada;
        this.model.capacidadeAjusteManual = calculada === null ? digitada !== null : digitada !== calculada;
      },

      usarCapacidadeCalculada() {
        if (this.capacidadeCalculada === null) return;
        this.textoCapacidade = String(this.capacidadeCalculada);
        this.model.capacidadeAjusteManual = false;
        this.anuncio = `Capacidade alterada para ${this.textoAlunos(this.capacidadeCalculada)}, pelo cálculo.`;
      },

      validar(): boolean {
        const erros: Partial<Record<CampoSala, string>> = {};
        const numero = (this.model.numero ?? '').trim();
        const bloco = (this.model.bloco ?? '').trim();

        if (!numero) erros.numero = 'Informe a identificação do ambiente.';
        if (!bloco) erros.bloco = 'Informe a localização do ambiente.';

        const area = paraNumero(this.textoArea);
        if (this.textoArea !== '' && area === null) erros.area = 'Informe a área como número.';
        else if (area !== null) {
          if (area <= 0) erros.area = 'A área deve ser maior que zero.';
          else if (area > AREA_MAXIMA) erros.area = 'A área pode ser de no máximo 32.767 m².';
          else if (casasDecimais(area) > CASAS_AREA) erros.area = 'A área aceita no máximo duas casas decimais.';
        }

        const coeficiente = paraNumero(this.textoCoeficiente);
        if (this.textoCoeficiente !== '' && coeficiente === null)
          erros.coeficienteAlunoM2 = 'Informe o coeficiente como número.';
        else if (coeficiente !== null) {
          if (coeficiente <= 0) erros.coeficienteAlunoM2 = 'O coeficiente de alunos por m² deve ser maior que zero.';
          else if (coeficiente > COEFICIENTE_MAXIMO)
            erros.coeficienteAlunoM2 = 'O coeficiente pode ser de no máximo 10 alunos por m².';
          else if (casasDecimais(coeficiente) > CASAS_COEFICIENTE)
            erros.coeficienteAlunoM2 = 'O coeficiente aceita no máximo quatro casas decimais.';
        }

        const capacidade = paraNumero(this.textoCapacidade);
        if (this.textoCapacidade !== '' && (capacidade === null || !Number.isInteger(capacidade)))
          erros.capacidade = 'A capacidade de alunos deve ser um número inteiro.';
        else if (capacidade !== null && (capacidade < 0 || capacidade > CAPACIDADE_MAXIMA))
          erros.capacidade = 'A capacidade de alunos deve estar entre 0 e 32.767.';

        const capacidadeReal = paraNumero(this.textoCapacidadeReal);
        if (this.textoCapacidadeReal !== '' && (capacidadeReal === null || !Number.isInteger(capacidadeReal)))
          erros.capacidadeReal = 'A capacidade real deve ser um número inteiro.';
        else if (capacidadeReal !== null && (capacidadeReal < 0 || capacidadeReal > CAPACIDADE_MAXIMA))
          erros.capacidadeReal = 'A capacidade real deve estar entre 0 e 32.767.';

        this.erros = erros;

        const primeiro = ORDEM_CAMPOS.find(([campo]) => erros[campo]);
        if (primeiro) {
          const quantidade = Object.keys(erros).length;
          this.anuncio = `O ambiente não foi salvo: ${pluralizar(quantidade, 'campo precisa', 'campos precisam')} de correção.`;
          this.$nextTick(() => document.getElementById(primeiro[1])?.focus());
          return false;
        }
        return true;
      },

      async salvar() {
        if (this.somenteLeitura || this.salvando || !this.validar()) return;

        // Cópia congelada no instante do clique (o axios serializa depois).
        const dados: SalaModel = {
          ...this.model,
          gesSalaId: this.idNumerico,
          gesEscolaId: useStore().getters.getCodigoEscola,
          numero: (this.model.numero ?? '').trim(),
          bloco: (this.model.bloco ?? '').trim(),
          area: paraNumero(this.textoArea),
          coeficienteAlunoM2: paraNumero(this.textoCoeficiente),
          capacidade: paraNumero(this.textoCapacidade),
          capacidadeReal: paraNumero(this.textoCapacidadeReal),
          capacidadeAjusteManual: !!this.model.capacidadeAjusteManual,
        };

        this.salvando = true;
        try {
          const { data } = await salaService.inserirAtualizar(dados);
          toastService.alertaSucesso('Ambiente salvo com sucesso!');
          this.anuncio = 'Ambiente salvo.';
          if (data?.codigo && Number(data.codigo) !== this.idNumerico) {
            await this.$router.replace({ name: 'sala_create', params: { id: data.codigo } });
          } else {
            await this.carregar();
          }
        } catch {
          // A mensagem do servidor (400) já sai pelo interceptor global.
          this.anuncio = 'O ambiente não foi salvo.';
        } finally {
          this.salvando = false;
        }
      },

      novoAmbiente() {
        if (this.idNumerico) {
          this.$router.replace({ name: 'sala_create' });
        } else {
          this.aplicarModelo(new SalaModel());
        }
      },

      async excluir() {
        if (!this.idNumerico || !this.permissaoTela.excluir) return;
        try {
          await salaService.remover(this.idNumerico);
          toastService.alertaSucesso('Ambiente excluído com sucesso!');
          this.$router.replace({ name: 'sala_create' });
        } catch {
          this.anuncio = 'O ambiente não foi excluído.';
        }
      },
    },
  });
</script>

<style scoped>
  .sala-ambiente .card {
    margin-bottom: 1rem;
  }

  /*
   * Situação da capacidade. O aviso de ajuste manual não depende só da cor (WCAG 1.4.1): tem
   * ícone de alerta, texto em negrito e a barra lateral. Cores dos tokens AAA do sistema.
   */
  .sala-capacidade-situacao {
    padding: 0.5rem 0.75rem;
    border-left: 4px solid var(--saeweb-aaa-informacao, currentColor);
  }

  .sala-capacidade-situacao--aviso {
    border-left: 6px solid var(--saeweb-aaa-atencao, currentColor);
    font-weight: 500;
  }
</style>
